import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, Save, Settings } from 'lucide-react';
import { useState } from 'react';

interface SchedulingSettings {
  maxAppointmentsPerDay: number;
  allowWeekends: boolean;
  businessHoursStart: string;
  businessHoursEnd: string;
  timeSlotDuration: number;
  bufferBetweenAppointments: number;
  advanceBookingDays: number;
  minAdvanceBookingHours: number;
  allowedDaysOfWeek: number[];
  blackoutDates: string[];
}

const getApiBase = () => {
  const base = import.meta.env.BASE_URL?.replace(/\/$/, '') || '';
  return `${base}/api`;
};

export default function AdminScheduling() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const { data: settings, isLoading } = useQuery<SchedulingSettings>({
    queryKey: ['scheduling-settings'],
    queryFn: async () => {
      const res = await fetch(`${getApiBase()}/settings/scheduling`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load settings');
      return res.json();
    },
  });

  const [formData, setFormData] = useState<SchedulingSettings | null>(null);

  // Initialize form data when settings load
  if (settings && !formData) {
    setFormData(settings);
  }

  const handleSave = async () => {
    if (!formData) return;

    setIsSaving(true);
    try {
      const res = await fetch(`${getApiBase()}/settings/scheduling`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error('Failed to save settings');

      toast({
        title: 'Settings Saved',
        description: 'Scheduling settings have been updated successfully.',
      });

      queryClient.invalidateQueries({ queryKey: ['scheduling-settings'] });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save scheduling settings.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof SchedulingSettings, value: any) => {
    if (!formData) return;
    setFormData({ ...formData, [field]: value });
  };

  const toggleDayOfWeek = (day: number) => {
    if (!formData) return;
    const days = formData.allowedDaysOfWeek;
    if (days.includes(day)) {
      updateField(
        'allowedDaysOfWeek',
        days.filter(d => d !== day)
      );
    } else {
      updateField('allowedDaysOfWeek', [...days, day].sort());
    }
  };

  if (isLoading || !formData) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="h-8 w-64 bg-slate-200 animate-pulse rounded" />
        <div className="h-96 bg-slate-100 animate-pulse rounded-2xl" />
      </div>
    );
  }

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-bebas text-4xl tracking-wide mb-2">
            SCHEDULING SETTINGS
          </h1>
          <p className="font-sans text-slate-500">
            Configure appointment booking rules and availability.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          size="lg"
          className="font-rajdhani font-bold tracking-wider"
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'SAVING...' : 'SAVE CHANGES'}
        </Button>
      </div>

      {/* Capacity Settings */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-rajdhani font-bold tracking-wider flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            CAPACITY & LIMITS
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="maxAppointments">
              Maximum Appointments Per Day
            </Label>
            <Input
              id="maxAppointments"
              type="number"
              min="1"
              max="100"
              value={formData.maxAppointmentsPerDay}
              onChange={e =>
                updateField('maxAppointmentsPerDay', parseInt(e.target.value))
              }
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Total number of appointments that can be booked per day across all
              courses.
            </p>
          </div>

          <div>
            <Label htmlFor="advanceBooking">
              Advance Booking Window (Days)
            </Label>
            <Input
              id="advanceBooking"
              type="number"
              min="1"
              max="365"
              value={formData.advanceBookingDays}
              onChange={e =>
                updateField('advanceBookingDays', parseInt(e.target.value))
              }
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              How far in advance customers can book appointments.
            </p>
          </div>

          <div>
            <Label htmlFor="minAdvance">Minimum Advance Notice (Hours)</Label>
            <Input
              id="minAdvance"
              type="number"
              min="0"
              max="168"
              value={formData.minAdvanceBookingHours}
              onChange={e =>
                updateField('minAdvanceBookingHours', parseInt(e.target.value))
              }
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">
              Minimum hours before an appointment can be booked (prevents
              last-minute bookings).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Business Hours */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-rajdhani font-bold tracking-wider flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            BUSINESS HOURS & TIME SLOTS
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startTime">Business Hours Start</Label>
              <Input
                id="startTime"
                type="time"
                value={formData.businessHoursStart}
                onChange={e =>
                  updateField('businessHoursStart', e.target.value)
                }
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="endTime">Business Hours End</Label>
              <Input
                id="endTime"
                type="time"
                value={formData.businessHoursEnd}
                onChange={e => updateField('businessHoursEnd', e.target.value)}
                className="mt-2"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="slotDuration">Time Slot Duration (Minutes)</Label>
            <Select
              value={formData.timeSlotDuration.toString()}
              onValueChange={value =>
                updateField('timeSlotDuration', parseInt(value))
              }
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="180">3 hours</SelectItem>
                <SelectItem value="240">4 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="buffer">
              Buffer Between Appointments (Minutes)
            </Label>
            <Select
              value={formData.bufferBetweenAppointments.toString()}
              onValueChange={value =>
                updateField('bufferBetweenAppointments', parseInt(value))
              }
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">No buffer</SelectItem>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              Time between appointments for setup/cleanup.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Available Days */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-rajdhani font-bold tracking-wider flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            AVAILABLE DAYS
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label>Allow Weekend Bookings</Label>
              <p className="text-xs text-slate-500 mt-1">
                Enable appointments on Saturdays and Sundays
              </p>
            </div>
            <Switch
              checked={formData.allowWeekends}
              onCheckedChange={checked => updateField('allowWeekends', checked)}
            />
          </div>

          <div>
            <Label>Allowed Days of Week</Label>
            <p className="text-xs text-slate-500 mb-3">
              Select which days customers can book appointments
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {daysOfWeek.map(day => (
                <div
                  key={day.value}
                  onClick={() => toggleDayOfWeek(day.value)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.allowedDaysOfWeek.includes(day.value)
                      ? 'border-primary bg-primary/10'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="font-rajdhani font-bold text-sm text-center">
                    {day.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          size="lg"
          className="font-rajdhani font-bold tracking-wider"
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'SAVING...' : 'SAVE ALL CHANGES'}
        </Button>
      </div>
    </div>
  );
}
